import DOMPurify from 'dompurify';

const FEATURE_ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'span',
  'ul', 'ol', 'li', 'a',
];

const TAB_HTML_ALLOWED_TAGS = [
  ...FEATURE_ALLOWED_TAGS,
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'figure', 'figcaption', 'blockquote', 'hr', 'div',
  'pre', 'code', 'mark',
];

const FEATURE_ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'style'];

const TAB_HTML_ALLOWED_ATTR = [
  ...FEATURE_ALLOWED_ATTR,
  'src', 'alt', 'width', 'height', 'colspan', 'rowspan',
];

const isAllowedClass = (value) => {
  const classes = String(value || '').split(/\s+/).filter(Boolean);
  return classes.every((cls) => /^ql-size-(small|large|huge)$/.test(cls) || cls.startsWith('sk-'));
};

const COLOR_VALUE = '(#[0-9a-f]{3,8}|rgb\\(\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*\\))';

const isAllowedStyleDeclaration = (part) => {
  const decl = String(part || '').trim().toLowerCase();
  if (!decl) return true;
  return /^font-size:\s*[\d.]+(px|em|rem|%)$/.test(decl)
    || /^text-align:\s*(left|right|center|justify)$/.test(decl)
    || new RegExp(`^color:\\s*${COLOR_VALUE}$`).test(decl)
    || new RegExp(`^background-color:\\s*${COLOR_VALUE}$`).test(decl);
};

const isAllowedStyle = (value) => {
  const style = String(value || '').trim();
  if (!style) return true;
  const parts = style.split(';').map((p) => p.trim()).filter(Boolean);
  return parts.every(isAllowedStyleDeclaration);
};

const isAllowedImgSrc = (value) => {
  const href = String(value || '').trim().toLowerCase();
  if (!href) return false;
  if (href.startsWith('javascript:') || href.startsWith('data:')) return false;
  return href.startsWith('http://') || href.startsWith('https://') || href.startsWith('/');
};

const addSanitizeHooks = () => {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'class' && !isAllowedClass(data.attrValue)) {
      data.attrValue = '';
      data.keepAttr = false;
    }

    if (data.attrName === 'style') {
      if (!isAllowedStyle(data.attrValue)) {
        data.keepAttr = false;
      }
    }

    if (data.attrName === 'href' && data.attrValue) {
      const href = String(data.attrValue).trim().toLowerCase();
      if (href.startsWith('javascript:') || href.startsWith('data:')) {
        data.keepAttr = false;
      }
    }

    if (data.attrName === 'src' && data.attrValue && !isAllowedImgSrc(data.attrValue)) {
      data.keepAttr = false;
    }
  });
};

let hooksRegistered = false;
const ensureHooks = () => {
  if (!hooksRegistered) {
    addSanitizeHooks();
    hooksRegistered = true;
  }
};

/** Plain-text content from HTML (for grouping/filtering). */
export const stripHtmlText = (html) => {
  const raw = String(html || '').trim();
  if (!raw) return '';

  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = raw;
    return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
  }

  return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const sanitizeWithTags = (html, allowedTags, allowedAttr) => {
  ensureHooks();
  const raw = String(html || '').trim();
  if (!raw) return '';

  if (!/[<>]/.test(raw)) {
    return DOMPurify.sanitize(raw, { ALLOWED_TAGS: [] });
  }

  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttr,
    ALLOW_DATA_ATTR: false,
  });
};

/** Sanitize product feature HTML for safe storefront rendering. */
export const sanitizeFeatureHtml = (html) =>
  sanitizeWithTags(html, FEATURE_ALLOWED_TAGS, FEATURE_ALLOWED_ATTR);

/** Sanitize full detail-tab HTML (tables, images, headings). */
export const sanitizeTabHtml = (html) =>
  sanitizeWithTags(html, TAB_HTML_ALLOWED_TAGS, TAB_HTML_ALLOWED_ATTR);
